import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const API = "/api/auditoria/criterio";
const API_TIPOS = "/api/auditoria/tipo-documento";

const authConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  },
});

const ESTADO_INICIAL = {
  tipoDocumentoObjectId: "",
  codigoCriterio: "",
  nombreCriterio: "",
  descripcionCriterio: "",
  campoDocumento: "",
  ordenCriterio: 0,
  obligatorioCriterio: true,
  estadoCriterio: "Activo",
  observacionCriterio: "",
};

const Auditoria_RegistrarCriterio = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const editando = useMemo(() => Boolean(id), [id]);

  const [form, setForm] = useState(ESTADO_INICIAL);
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cargando, setCargando] = useState(false);

  const manejarError = useCallback(
    (error, mensaje) => {
      if (error?.response?.status === 401) {
        localStorage.removeItem("token");
        Swal.fire("Sesión expirada", "Inicia sesión nuevamente.", "warning").then(
          () => navigate("/login")
        );
        return;
      }

      Swal.fire(
        "Error",
        error?.response?.data?.message || mensaje || "Ocurrió un error.",
        "error"
      );
    },
    [navigate]
  );

  const cargarBase = useCallback(async () => {
    try {
      setCargando(true);

      const peticiones = [axios.get(API_TIPOS, authConfig())];

      if (editando) {
        peticiones.push(axios.get(`${API}/${id}`, authConfig()));
      }

      const respuestas = await Promise.all(peticiones);

      setTipos(
        Array.isArray(respuestas[0].data?.tiposDocumento)
          ? respuestas[0].data.tiposDocumento
          : []
      );

      if (editando) {
        const item = respuestas[1].data?.criterio;

        if (!item) throw new Error("Criterio no encontrado.");

        setForm({
          tipoDocumentoObjectId:
            item.tipoDocumentoObjectId?._id ||
            item.tipoDocumentoObjectId ||
            "",
          codigoCriterio: item.codigoCriterio || "",
          nombreCriterio: item.nombreCriterio || "",
          descripcionCriterio: item.descripcionCriterio || "",
          campoDocumento: item.campoDocumento || "",
          ordenCriterio: item.ordenCriterio ?? 0,
          obligatorioCriterio: item.obligatorioCriterio !== false,
          estadoCriterio: item.estadoCriterio || "Activo",
          observacionCriterio: item.observacionCriterio || "",
        });
      }
    } catch (error) {
      manejarError(error, "No se pudieron cargar los datos.");
    } finally {
      setCargando(false);
    }
  }, [editando, id, manejarError]);

  useEffect(() => {
    cargarBase();
  }, [cargarBase]);

  const cambiar = (campo, valor) => {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  };

  const guardar = async (e) => {
    e.preventDefault();

    if (
      !form.tipoDocumentoObjectId ||
      !form.codigoCriterio.trim() ||
      !form.nombreCriterio.trim()
    ) {
      Swal.fire(
        "Atención",
        "Tipo de documento, código y nombre son obligatorios.",
        "warning"
      );
      return;
    }

    try {
      setLoading(true);

      if (editando) {
        await axios.put(`${API}/${id}`, form, authConfig());
      } else {
        await axios.post(API, form, authConfig());
      }

      await Swal.fire(
        "Correcto",
        editando ? "Criterio actualizado." : "Criterio registrado.",
        "success"
      );

      navigate("/auditoria/criterio");
    } catch (error) {
      manejarError(error, "No se pudo guardar el criterio.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="card shadow-sm">
        <div className="card-header">
          <h4 className="mb-0">
            {editando ? "Actualizar criterio" : "Registrar criterio"}
          </h4>
        </div>

        <form onSubmit={guardar}>
          <div className="card-body">
            {cargando ? (
              <div className="text-center py-4">Cargando...</div>
            ) : (
              <div className="row g-3">
                <div className="col-md-7">
                  <label className="form-label">Tipo de documento *</label>
                  <select
                    className="form-select"
                    value={form.tipoDocumentoObjectId}
                    onChange={(e) =>
                      cambiar("tipoDocumentoObjectId", e.target.value)
                    }
                    required
                  >
                    <option value="">Seleccione...</option>
                    {tipos.map((tipo) => (
                      <option key={tipo._id} value={tipo._id}>
                        {tipo.nombreTipoDocumento}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-5">
                  <label className="form-label">Código *</label>
                  <input
                    className="form-control"
                    value={form.codigoCriterio}
                    onChange={(e) =>
                      cambiar(
                        "codigoCriterio",
                        e.target.value.toUpperCase().replace(/\s+/g, "_")
                      )
                    }
                    required
                  />
                </div>

                <div className="col-md-8">
                  <label className="form-label">Nombre del criterio *</label>
                  <input
                    className="form-control"
                    value={form.nombreCriterio}
                    onChange={(e) => cambiar("nombreCriterio", e.target.value)}
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Orden</label>
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    value={form.ordenCriterio}
                    onChange={(e) =>
                      cambiar("ordenCriterio", Number(e.target.value))
                    }
                  />
                </div>

                <div className="col-md-8">
                  <label className="form-label">
                    Campo relacionado del documento
                  </label>
                  <input
                    className="form-control font-monospace"
                    value={form.campoDocumento}
                    onChange={(e) => cambiar("campoDocumento", e.target.value)}
                    placeholder="Ej. firmaInstructor"
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Estado</label>
                  <select
                    className="form-select"
                    value={form.estadoCriterio}
                    onChange={(e) => cambiar("estadoCriterio", e.target.value)}
                  >
                    <option value="Activo">Activo</option>
                    <option value="Inactivo">Inactivo</option>
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label">Descripción</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={form.descripcionCriterio}
                    onChange={(e) =>
                      cambiar("descripcionCriterio", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-4">
                  <div className="form-check mt-2">
                    <input
                      id="obligatorioCriterio"
                      type="checkbox"
                      className="form-check-input"
                      checked={form.obligatorioCriterio}
                      onChange={(e) =>
                        cambiar("obligatorioCriterio", e.target.checked)
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="obligatorioCriterio"
                    >
                      Criterio obligatorio
                    </label>
                  </div>
                </div>

                <div className="col-12">
                  <label className="form-label">Observación</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={form.observacionCriterio}
                    onChange={(e) =>
                      cambiar("observacionCriterio", e.target.value)
                    }
                  />
                </div>
              </div>
            )}
          </div>

          <div className="card-footer d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate("/auditoria/criterio")}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || cargando}
            >
              {loading ? "Guardando..." : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Auditoria_RegistrarCriterio;
